<template>
  <v-container>
    <div
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        color="#2661FF"
        size="80"
      ></v-progress-circular>
    </div>
    <div v-else>
      <v-row class="px-2">
        <v-col cols="12" md="6">
          <div
            class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6"
            style="font-size: 20px !important;"
          >
            {{ $t('pricing_model.title') }}
          </div>
          <v-breadcrumbs
            divider=">"
            :items="breadcrumbItems"
            class="py-2"
          ></v-breadcrumbs>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            class="rounded-lg mb-2"
            color="#2661FF"
            @click="redirectCreate"
          >
            <v-icon color="white">add</v-icon>
            <span style="color: white;"
              >{{ $t('pricing_model.create.title') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg" flat>
            <v-card-text class="px-10 pt-8">
              <v-row>
                <v-col cols="6" class="d-flex justify-start align-center">
                  <p class="grey--text text--darken-2 pt-2">
                    {{ $t('pricing_model.description') }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
              <v-row>
                <v-col cols="3" class="ml-6">
                  <v-text-field
                    v-model="pricingModelsTable.search"
                    outlined
                    class="rounded-lg font-weight-regular"
                    :label="$t('actions.search')"
                    prepend-inner-icon="search"
                    clearable
                    @keyup.enter="fetchPricingModels"
                    @click:clear="clearSearch"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-data-table
                :loading="pricingModelsTable.loading"
                :headers="pricingModelsTable.headers"
                :items="pricingModelsTable.items"
                :server-items-length="pricingModelsTable.total"
                :items-per-page="pricingModelsTable.pagination.itemsPerPage"
                :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
                :no-data-text="$t('subscriptions_report.no_data')"
                :no-results-text="$t('subscriptions_report.no_data')"
                @update:options="pricingModelsTable.pagination = $event"
              >
                <!-- SHOWS NO DATA -->
                <template v-slot:no-data>
                  <div class="text-center pa-5 my-5">
                    <v-img
                      :src="require('@/assets/svg/box_empty.svg')"
                      height="150"
                      max-height="150px"
                      aspect-ratio="1"
                      contain
                    />
                    <div class="grey--text">
                      {{ $t('subscriptions_report.no_data') }}
                    </div>
                  </div>
                </template>

                <template
                  v-if="pricingModelsTable.items.length"
                  v-slot:body="{ items }"
                >
                  <tbody class="text-center">
                    <tr
                      v-for="item in items"
                      :key="item.id"
                      class="cursor-pointer"
                    >
                      <td @click="open(item.id)">{{ item.id }}</td>
                      <td @click="open(item.id)">{{ item.display_name }}</td>
                      <td @click="open(item.id)">{{ item.description }}</td>
                      <td @click="open(item.id)">
                        {{
                          item.__meta__.totalOrganizations
                            ? item.__meta__.totalOrganizations
                            : 0
                        }}
                      </td>
                      <td @click="open(item.id)">
                        {{
                          item.total_collected
                            | valueFormatCurrency(usdCurrency.symbol)
                        }}
                        {{ usdCurrency.code }}
                      </td>
                      <td @click="open(item.id)">
                        {{
                          item.average_by_client
                            | valueFormatCurrency(usdCurrency.symbol)
                        }}
                        {{ usdCurrency.code }}
                      </td>
                      <td class="d-flex justify-center align-center">
                        <v-tooltip bottom color="grey darken-4">
                          <template v-slot:activator="{ on }">
                            <v-btn text fab x-small @click="open(item.id)">
                              <v-icon
                                v-on="on"
                                class="grey--text text--darken-3"
                                size="24"
                              >
                                visibility
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('common.view') }}</span>
                        </v-tooltip>
                        <v-tooltip
                          bottom
                          color="grey darken-4"
                          v-if="!item.special_pricing"
                        >
                          <template v-slot:activator="{ on }">
                            <v-btn @click="edit(item.id)" text fab x-small>
                              <v-icon
                                v-on="on"
                                class="grey--text text--darken-3"
                                size="24"
                              >
                                edit_square
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('general.edit') }}</span>
                        </v-tooltip>
                        <v-tooltip
                          bottom
                          color="grey darken-4"
                          v-if="!item.special_pricing"
                        >
                          <template v-slot:activator="{ on }">
                            <v-btn
                              @click="deletePricing(item.id)"
                              text
                              fab
                              x-small
                            >
                              <v-icon
                                v-on="on"
                                class="grey--text text--darken-3"
                                size="24"
                              >
                                delete
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('general.delete') }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <!-- EDIT -->
    <v-navigation-drawer
      v-if="pricingModels && pricingModel && Object.keys(pricingModel).length"
      :value="pricingModelDrawerEdit"
      @input="inputPricingModelDrawerEdit"
      :width="'90%'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
    >
      <EditPricingDrawer />
    </v-navigation-drawer>

    <!-- VIEW -->
    <v-navigation-drawer
      v-if="pricingModels && pricingModel && Object.keys(pricingModel).length"
      :value="pricingModelDrawerView"
      @input="inputPricingModelDrawerView"
      :width="'90%'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
    >
      <PricingModelDetail />
    </v-navigation-drawer>

    <!-- DELETE -->
    <DeletePricingDialog />
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import EditPricingDrawer from '@/components/PricingModels/Edit/EditPricingDrawer'
import PricingModelDetail from '@/components/PricingModels/View/PricingModelDetail'
import DeletePricingDialog from '@/components/PricingModels/Delete/DeletePricingDialog'

export default {
  name: 'organizationPricingModels',
  components: {
    EditPricingDrawer,
    PricingModelDetail,
    DeletePricingDialog
  },
  data () {
    return {
      isLoading: false,
      breadcrumbItems: [
        { text: this.$t('organization.title'), disabled: true },
        {
          text: this.$t('pricing_model.title'),
          disabled: false
        }
      ],
      pricingModelsTable: {
        loading: false,
        items: [],
        total: 0,
        search: null,
        pagination: {
          sortBy: ['id'],
          sortDesc: [false],
          itemsPerPage: 10
        },
        headers: [
          {
            text: 'ID',
            align: 'center',
            sortable: true,
            value: 'id',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('pricing_model.table.headers.pricing'),
            align: 'center',
            sortable: true,
            value: 'display_name',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('pricing_model.table.headers.description'),
            align: 'center',
            sortable: false,
            value: 'description',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t(
              'pricing_model.table.headers.organizations_with_pricing'
            ),
            align: 'center',
            sortable: false,
            value: '',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('pricing_model.table.headers.total_collected'),
            align: 'center',
            sortable: false,
            value: '',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('pricing_model.table.headers.average_by_org'),
            align: 'center',
            sortable: false,
            value: '',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('pricing_model.table.headers.actions'),
            align: 'center',
            sortable: false,
            value: '',
            class: 'text-no-wrap header-color'
          }
        ]
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      await this.getCurrencies()
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },
  watch: {
    'pricingModelsTable.pagination': {
      async handler () {
        await this.fetchPricingModels()
      },
      deep: true
    },
    async reloadRequired (value) {
      if (value) {
        await this.fetchPricingModels()
        this.setReloadRequired(false)
      }
    }
  },
  computed: {
    ...mapState({
      pricingModels: ({ pricingModels: { pricingModels } }) => pricingModels,
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel,
      pricingModelsTotal: ({ pricingModels: { pricingModelsTotal } }) =>
        pricingModelsTotal,
      currencies: ({ countries: { currencies } }) => currencies,
      pricingModelDrawerEdit: ({ pricingModels: { pricingModelDrawerEdit } }) =>
        pricingModelDrawerEdit,
      pricingModelDrawerView: ({ pricingModels: { pricingModelDrawerView } }) =>
        pricingModelDrawerView,
      reloadRequired: ({ pricingModels: { reloadRequired } }) => reloadRequired
    }),
    usdCurrency () {
      const usd_currency_code = 'USD'
      return this.currencies.find(curr => curr.code === usd_currency_code)
    }
  },
  methods: {
    ...mapActions({
      getPricingModels: 'pricingModels/getPricingModels',
      getPricingModel: 'pricingModels/getPricingModel',
      getCurrencies: 'countries/getCurrencies'
    }),
    ...mapMutations({
      setPricingModelDrawerEdit: 'pricingModels/setPricingModelDrawerEdit',
      setPricingModelDrawerView: 'pricingModels/setPricingModelDrawerView',
      setPricingModelDialogDelete: 'pricingModels/setPricingModelDialogDelete',
      resetPricingModel: 'pricingModels/resetPricingModel',
      setReloadRequired: 'pricingModels/setReloadRequired'
    }),
    async fetchPricingModels () {
      try {
        this.pricingModelsTable.loading = true
        let { sortBy, sortDesc = false } = this.pricingModelsTable.pagination
        const { page, itemsPerPage } = this.pricingModelsTable.pagination
        const { search } = this.pricingModelsTable

        if (Array.isArray(sortBy)) {
          ;[sortBy] = sortBy
        }
        if (Array.isArray(sortDesc)) {
          ;[sortDesc] = sortDesc
        }

        await this.getPricingModels({
          search,
          page,
          rowsPerPage: itemsPerPage,
          sortBy: sortBy,
          descending: sortDesc
        })

        this.pricingModelsTable.items = this.pricingModels
        this.pricingModelsTable.total = Number(
          parseInt(this.pricingModelsTotal, 10)
        )
      } catch (error) {
        console.log(error)
      } finally {
        this.pricingModelsTable.loading = false
      }
    },
    redirectCreate () {
      this.$router.replace({ name: 'organizationPricingModelsCreate' })
    },
    async clearSearch () {
      this.pricingModelsTable.search = null
      await this.fetchPricingModels()
    },
    // EDIT
    async edit (pricing_model_id) {
      await this.getPricingModel({ id: pricing_model_id })
      this.setPricingModelDrawerEdit(true)
    },
    inputPricingModelDrawerEdit (arg) {
      if (!arg) {
        this.setPricingModelDrawerEdit(false)
        this.resetPricingModel()
      }
    },
    // DETAILS
    async open (pricing_model_id) {
      await this.getPricingModel({ id: pricing_model_id })
      this.setPricingModelDrawerView(true)
    },
    inputPricingModelDrawerView (arg) {
      if (!arg) {
        this.setPricingModelDrawerView(false)
        this.resetPricingModel()
      }
    },
    // DELETE
    async deletePricing (pricing_model_id) {
      await this.getPricingModel({ id: pricing_model_id })
      this.setPricingModelDialogDelete(true)
    }
  }
}
</script>

<style lang="scss">
.header-color {
  color: #2661ff !important;
}
</style>
